import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { formatInteger } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, IconButton, Overlay, Skeleton } from '../../../ds/components';
import { useEscapeKey, useFocusTrap, useIsMobile } from '../../../ds/hooks';
import { lockBodyScroll } from '../../../ds/components/Sheet';
import { errorMessage } from '../../../lib/api';
import { useDealHistory } from '../api';
import type { HistoryEntry, Tone } from '../types';
import { shortDayOf } from '../format';
import styles from './dialogs.module.css';

/**
 * Deal-Pipeline 23 „Stage-Historie": the 420 px side panel with the deal's history grouped by day
 * (`GET /deals/:id/history`), newest first, and „Deal öffnen" in the footer.
 */

export interface HistoryPanelProps {
  open: boolean;
  dealId: string;
  contactName: string;
  /** „WE 1.02" */
  line: string;
  onClose: () => void;
  onOpenDeal: () => void;
}

const dotClass: Record<Tone, string | undefined> = {
  accent: styles.dotAccent,
  info: styles.dotInfo,
  danger: styles.dotDanger,
  success: styles.dotSuccess,
  warning: styles.dotWarning,
  neutral: undefined,
};

function entriesText(total: number): string {
  return total === 1 ? '1 Eintrag' : `${formatInteger(total)} Einträge`;
}

export function HistoryPanel({ open, dealId, contactName, line, onClose, onOpenDeal }: HistoryPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const mobile = useIsMobile();
  const history = useDealHistory(dealId, 'all', open);
  useEscapeKey(onClose, open);
  useFocusTrap(panelRef, open, { initialFocus: 'container' });
  useEffect(() => (open ? lockBodyScroll() : undefined), [open]);

  if (!open) return null;
  const days = history.data?.days ?? [];
  const flat: { day: string; entry: HistoryEntry }[] = days.flatMap((day) =>
    day.entries.map((entry) => ({ day: day.day, entry })),
  );

  return createPortal(
    <Overlay top={mobile ? 0 : 60} onScrimClick={onClose}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Stage-Historie"
        tabIndex={-1}
        className={styles.historyPanel}
        style={mobile ? { top: 0, width: '100%' } : undefined}
      >
        <div className={styles.historyHead}>
          <div>
            <div className={styles.historyTitle}>Stage-Historie</div>
            <div className={styles.historySub}>
              {contactName} · {line}
            </div>
          </div>
          <IconButton
            icon={X}
            label="Schließen"
            variant="ghost"
            size={28}
            className={styles.historyClose}
            onClick={onClose}
          />
        </div>
        <div className={styles.historyBody}>
          {history.isPending ? (
            <>
              <Skeleton variant="line" width={140} />
              <Skeleton variant="block" height={44} style={{ marginTop: 12 }} />
              <Skeleton variant="block" height={44} style={{ marginTop: 12 }} />
            </>
          ) : null}
          {history.isError ? <Banner tone="danger">{errorMessage(history.error)}</Banner> : null}
          {history.data && flat.length === 0 ? (
            <div className={styles.historyEmpty}>Noch keine Einträge.</div>
          ) : null}
          {days.map((day) => (
            <div key={day.day}>
              <div className={styles.historyDay}>{day.label}</div>
              {day.entries.map((entry) => {
                const last = flat[flat.length - 1]?.entry.id === entry.id;
                return (
                  <div key={entry.id} className={clsx(styles.historyEntry, last && styles.historyEntryLast)}>
                    <span className={clsx(styles.historyDot, dotClass[entry.tone])} aria-hidden="true" />
                    <div className={styles.historyText}>
                      <div className={styles.historyRow}>
                        <span
                          className={clsx(
                            styles.historyEntryTitle,
                            entry.tone === 'info' && styles.titleInfo,
                            entry.tone === 'danger' && styles.titleDanger,
                          )}
                        >
                          {entry.title}
                        </span>
                        <span className={styles.historyTime}>
                          {shortDayOf(day.day)}, {entry.time}
                        </span>
                      </div>
                      <div className={styles.historyDetail}>
                        {[entry.actorShort, entry.text].filter((part) => part && part.length > 0).join(' · ')}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className={styles.historyFoot}>
          <span className={styles.historyFootNote}>
            {history.data ? `${entriesText(history.data.total)} · unveränderlich` : ''}
          </span>
          <span className={styles.historyFootActions}>
            <Button variant="secondary" size="small" onClick={onOpenDeal}>
              Deal öffnen
            </Button>
          </span>
        </div>
      </div>
    </Overlay>,
    document.body,
  );
}
